/**
 * EmailTemplateDialog
 *
 * Reusable dialog that lets the user pick an email template (or compose blank)
 * for a target organisation. Optionally accepts a specific `contact` override
 * so per-contact "Send Email" buttons send to that contact rather than the
 * org's primary contact.
 *
 * Opens the global slide-in compose drawer (see ComposeEmailContext) instead
 * of navigating to /email-hub, so operators stay on the page they were
 * working on and can minimise the draft while making further edits.
 */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { Send, PenLine, FileText, Loader2 } from 'lucide-react';
import { useComposeEmail } from '../context/ComposeEmailContext';

// Templates support {{Org_name}} and {{Pri_contact}} placeholders. We
// substitute them client-side so the compose drawer shows the resolved
// preview immediately rather than the raw token.
const replaceTemplateVariables = (text, orgName, contactName) => {
  if (!text) return text;
  return String(text)
    .replace(/\{\{Org_name\}\}/gi, orgName || '')
    .replace(/\{\{Pri_contact\}\}/gi, contactName || '');
};

export const EmailTemplateDialog = ({
  open,
  onOpenChange,
  org,           // organisation object
  contact,       // optional specific contact override
  templates,
  loading,
}) => {
  const { openCompose } = useComposeEmail();

  const buildEmailPrefill = (template = null) => {
    if (!org) return null;
    const targetContact = contact ||
      (org.contacts?.find?.(c => c.is_primary)) ||
      (org.contacts?.[0]);
    const primaryContact =
      (org.contacts?.find?.(c => c.is_primary)) ||
      targetContact ||
      null;
    const orgName = org.name || '';
    const contactName = primaryContact?.name || targetContact?.name || 'there';

    let subject = '';
    let body = '';
    let attachments = [];
    let template_id = null;
    let ai_polished = false;

    if (template) {
      subject = replaceTemplateVariables(template.subject || '', orgName, contactName);
      body = replaceTemplateVariables(template.body || '', orgName, contactName);
      if (orgName && !subject.toLowerCase().includes(orgName.toLowerCase())) {
        subject = `${orgName} - ${subject}`;
      }
      attachments = Array.isArray(template.attachments) ? template.attachments : [];
      template_id = template.template_id || null;
    } else {
      body = `Dear ${contactName},

I hope this email finds you well.

${orgName ? `I am reaching out regarding ${orgName}.` : ''}

[Your message here]

Best regards,
G.S Workflow Team`;
      subject = orgName ? `${orgName} - ` : '';
    }

    return {
      to_email: targetContact?.email || org.email || '',
      to_name: targetContact?.name || '',
      org_id: org.org_id,
      org_name: orgName,
      contact_id: targetContact?.contact_id || '',
      subject,
      body,
      attachments,
      template_id,
      ai_polished,
    };
  };

  const launchCompose = (template = null) => {
    const prefill = buildEmailPrefill(template);
    if (!prefill) return;
    onOpenChange?.(false);
    openCompose(prefill);
  };

  const recipientLabel = contact
    ? `${contact.name || 'Contact'} (${contact.email || 'no email'})`
    : org?.name || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] sm:max-w-lg max-h-[85vh] overflow-y-auto p-6"
        data-testid="email-template-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-start gap-2 break-words">
            <Send className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span className="min-w-0 break-words">
              Send Email to {recipientLabel}
            </span>
          </DialogTitle>
          {contact && org && (
            <p className="text-xs text-muted-foreground truncate" title={org.name}>
              Organisation: {org.name}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            Choose to use a template with auto-filled variables or compose a new email.
          </p>

          <div
            className="p-4 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
            onClick={() => launchCompose(null)}
            data-testid="compose-new-email-btn"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <PenLine className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-medium">Compose New Email</h4>
                <p className="text-sm text-muted-foreground">
                  Start with a blank email pre-filled with contact details
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">Or use a template:</h4>
              {!loading && templates && templates.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {templates.length} template{templates.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {loading ? (
              <div className="text-center py-4">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : !templates || templates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No templates available. Create templates in Email Hub.
              </p>
            ) : (
              // ~5 visible rows; the rest scroll. Sized via a hard
              // max-height + scrollbar so the modal itself doesn't grow
              // off-screen when there are 20+ templates.
              <div
                className="space-y-2 max-h-[18rem] overflow-y-auto pr-1
                           [&::-webkit-scrollbar]:w-1.5
                           [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30
                           [&::-webkit-scrollbar-thumb]:rounded-full"
                data-testid="email-template-list"
              >
                {templates.map((template) => (
                  <div
                    key={template.template_id}
                    className="p-3 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                    onClick={() => launchCompose(template)}
                    data-testid={`template-${template.template_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-muted rounded">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium truncate">{template.name}</h5>
                        <p className="text-xs text-muted-foreground truncate">
                          {template.subject || 'No subject'}
                        </p>
                      </div>
                      {template.category && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          {template.category.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            <strong>Tip:</strong> Templates support variables:{' '}
            <code className="bg-muted px-1 rounded">{'{{Org_name}}'}</code> and{' '}
            <code className="bg-muted px-1 rounded">{'{{Pri_contact}}'}</code>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailTemplateDialog;
